import { createFileRoute, notFound } from '@tanstack/react-router'
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/src/components/ui/accordion'
import { Card, CardContent, CardHeader, CardTitle } from '@/src/components/ui/card'
import { Input } from '@/src/components/ui/input'
import { MagnifyingGlassIcon, EnvelopeIcon } from '@phosphor-icons/react'
import { useState, useMemo } from 'react'
import { useTranslation } from '@/src/i18n/client'
import { getStaticPage } from '@/src/lib/static-pages'
import { parseFaqMarkdown, type FaqCategory } from '@/src/lib/parse-faq-markdown'
import { NotFound } from '@/src/components/NotFound'

export const Route = createFileRoute('/_static/faq')({
  loader: () => {
    const enPage = getStaticPage('faq', 'en')
    const dePage = getStaticPage('faq', 'de')
    
    if (!enPage && !dePage) {
      throw notFound()
    }
    
    return { enPage, dePage }
  },
  head: () => ({
    meta: [
      { title: 'Frequently Asked Questions - GearLens' },
      { name: 'description', content: 'Find answers to common questions about GearLens gear drawing analysis service.' }
    ],
  }),
  component: FaqPage,
})

function FaqPage() {
  const { t, i18n } = useTranslation('static')
  const { enPage, dePage } = Route.useLoaderData()
  const [searchQuery, setSearchQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<string | null>(null)

  // Get current page based on locale
  const currentLocale = i18n.language.split('-')[0]
  const page = currentLocale === 'de' ? dePage : enPage

  // Parse markdown into FAQ categories
  const FAQ_CATEGORIES = useMemo(() => {
    return page ? parseFaqMarkdown(page.content) : []
  }, [page])

  const filteredCategories = useMemo(() => {
    if (!searchQuery && !activeCategory) {
      return FAQ_CATEGORIES
    }

    return FAQ_CATEGORIES.map(category => {
      if (activeCategory && category.id !== activeCategory) {
        return null
      }

      const filteredQuestions = category.questions.filter(q =>
        q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )

      if (filteredQuestions.length === 0) return null

      return {
        ...category,
        questions: filteredQuestions
      }
    }).filter(Boolean) as FaqCategory[]
  }, [FAQ_CATEGORIES, searchQuery, activeCategory])

  if (!page) {
    return <NotFound />
  }

  return (
    <div className="container mx-auto max-w-7xl py-8">
      {/* Header */}
      <div className="mb-12 text-center">
        {/* <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
          <MagnifyingGlassIcon size={32} className="text-primary" weight="bold" />
        </div> */}
        <h1 className="text-5xl font-bold mb-4">
          {t('faq.title')}
        </h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          {t('faq.subtitle')}
        </p>
      </div>

      {/* Search Bar */}
      <div className="mb-8 max-w-2xl mx-auto">
        <div className="relative">
          <MagnifyingGlassIcon
            size={20} 
            className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" 
          />
          <Input
            type="text"
            placeholder={t('faq.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-12 text-base"
          />
        </div>
      </div>

      {/* Category Filters */}
      {!searchQuery && (
        <div className="mb-8 flex flex-wrap gap-2 justify-center">
          <button
            onClick={() => setActiveCategory(null)}
            className={`px-3 py-1 rounded font-medium text-sm transition-colors ${
              activeCategory === null
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-foreground hover:bg-muted/70'
            }`}
          >
            {t('faq.allCategories')}
          </button>
          {FAQ_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
              className={`px-3 py-1 rounded font-medium text-sm transition-colors ${
                activeCategory === cat.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-muted/70'
              }`}
            >
              {cat.title}
            </button>
          ))}
        </div>
      )}

      {/* FAQ Categories */}
      <div className="space-y-8">
        {filteredCategories.map((category) => (
          <Card key={category.id} className="shadow-md border">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl">{category.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion className="w-full">
                {category.questions.map((faq, idx) => (
                  <AccordionItem key={idx} value={String(idx)}>
                    <AccordionTrigger className="text-left text-base font-medium hover:no-underline">
                      <span className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-xs bg-primary shrink-0" />
                        {faq.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm leading-relaxed pt-1 pl-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* No Results */}
      {filteredCategories.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
            <MagnifyingGlassIcon size={48} className="text-muted-foreground" />
          </div>
          <h3 className="text-xl font-semibold mb-2">{t('faq.noResultsTitle')}</h3>
          <p className="text-muted-foreground mb-6">
            {t('faq.noResultsDescription')}
          </p>
          <button
            onClick={() => {
              setSearchQuery('')
              setActiveCategory(null)
            }}
            className="text-blue-600 hover:underline font-medium"
          >
            {t('faq.clearSearch')}
          </button>
        </div>
      )}

      {/* Contact CTA */}
      <Card className="mt-12 bg-muted/50 border">
        <CardContent className="py-8 text-center">
          <EnvelopeIcon size={48} className="mx-auto mb-4 text-primary" weight="bold" />
          <h3 className="text-2xl font-bold mb-2">{t('faq.stillHaveQuestions')}</h3>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            {t('faq.stillHaveQuestionsDescription')}
          </p>
          <a
            href="mailto:support@gearlens.com"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
          >
            <EnvelopeIcon size={20} weight="bold" />
            {t('faq.contactSupport')}
          </a>
        </CardContent>
      </Card>
    </div>
  )
}
